import { openDB, type DBSchema } from "idb";

/**
 * La coda di partenza dell'app, unica per tutto quello che scrive.
 *
 * Regola del posto: ogni azione che scrive sul server passa di qui quando la rete manca.
 * Non e una coda per le richieste materiali, ne una coda per le foto: e la coda dell'app.
 * Una funzione nuova non deve aggiungere niente, perche il punto di passaggio non e la
 * schermata ma il link tRPC (`offline-link.ts`) e il caricamento file (`offline-upload.ts`).
 *
 * Ciclo di vita di un elemento, senza eccezioni:
 * 1. registrato sul dispositivo quando la rete manca,
 * 2. inviato da solo al ritorno della rete, nell'ordine in cui e stato registrato,
 * 3. cancellato dal dispositivo appena il server conferma di averlo ricevuto.
 *
 * Chi fallisce per un motivo che non e la rete non resta in coda per sempre: dopo la soglia
 * di tentativi diventa `blocked`, esce dal giro automatico e viene mostrato a chi lo ha
 * registrato, che lo riprova o lo elimina con le sue mani.
 */

export type OutboxOwner = { companyId: string; userId: string };

export type OutboxStatus = "pending" | "blocked";

/**
 * Il file in attesa di partire.
 *
 * Si tiene come byte grezzi e non come `Blob`: un `Blob` dentro IndexedDB non e sopravvissuto
 * allo spegnimento su tutti i browser, e una fotografia di cantiere persa e lavoro perso.
 * Il `Blob` si ricostruisce al momento dell'invio, che e l'unico momento in cui serve.
 */
export type OutboxAttachment = {
  /** Nome del campo del form multipart, quello che il server si aspetta. */
  field: string;
  filename: string;
  mimeType: string;
  bytes: ArrayBuffer;
};

export type OutboxOperation = {
  id: string;
  /** Numero crescente: e questo, non l'orologio, a decidere l'ordine di partenza. */
  sequence: number;
  companyId: string;
  userId: string;
  kind: "trpc" | "upload";
  /** Per `trpc` il percorso della procedura, per `upload` l'indirizzo di caricamento. */
  target: string;
  /** Corpo gia serializzato come viaggia sul filo, cosi il rinvio e identico all'invio. */
  body: string | null;
  attachment: OutboxAttachment | null;
  /** Elemento della stessa coda che deve arrivare prima, perche ne completa l'indirizzo. */
  dependsOn: string | null;
  /** Campo da leggere nel risultato di `dependsOn`, in forma puntata, esempio `request.id`. */
  dependsOnField: string | null;
  /** Frase breve in italiano, quella che legge chi guarda la coda. */
  label: string;
  attempts: number;
  status: OutboxStatus;
  lastError: string | null;
  nextAttemptAt: string;
  createdAt: string;
};

export type NewOutboxOperation = {
  companyId: string;
  userId: string;
  kind: "trpc" | "upload";
  target: string;
  body?: string | null;
  attachment?: OutboxAttachment | null;
  dependsOn?: string | null;
  dependsOnField?: string | null;
  label: string;
  createdAt?: string;
};

export type OutboxSender = {
  sendTrpc: (path: string, body: string) => Promise<unknown>;
  sendUpload: (url: string, attachment: OutboxAttachment) => Promise<unknown>;
};

export type FlushSummary = {
  /** Arrivate al server e cancellate dal dispositivo. */
  inviate: number;
  /** Ancora in coda: rete assente, oppure attesa fra un tentativo e l'altro. */
  rimandate: number;
  /** Fuori dal giro automatico, da guardare a mano. */
  segnalate: number;
};

/**
 * Errore della rete, non del server.
 *
 * Chi invia lo solleva quando la richiesta non e mai arrivata a destinazione. La coda lo
 * tratta in modo opposto a un rifiuto del server: non conta un tentativo, non avvicina la
 * soglia, e ferma il giro perche anche gli elementi dopo troverebbero la stessa rete spenta.
 */
export class NetworkUnavailableError extends Error {
  constructor(message = "Rete non disponibile.") {
    super(message);
    this.name = "NetworkUnavailableError";
  }
}

/** Oltre questa soglia l'elemento si segnala invece di restare in coda per sempre. */
export const MAX_ATTEMPTS = 5;

const RETRY_BASE_MILLISECONDS = 30_000;
const RETRY_MAX_MILLISECONDS = 60 * 60_000;
const DATABASE_NAME = "manutio-outbox";
const STORE = "operations";

interface OutboxDatabase extends DBSchema {
  [STORE]: {
    key: string;
    value: OutboxOperation;
    indexes: { sequence: number; owner: [string, string] };
  };
}

let databasePromise: Promise<Awaited<ReturnType<typeof openDatabase>>> | null = null;

function openDatabase() {
  return openDB<OutboxDatabase>(DATABASE_NAME, 1, {
    upgrade(database) {
      const store = database.createObjectStore(STORE, { keyPath: "id" });
      store.createIndex("sequence", "sequence");
      store.createIndex("owner", ["companyId", "userId"]);
    },
  });
}

function database() {
  databasePromise ??= openDatabase();
  return databasePromise;
}

const listeners = new Set<() => void>();

/** Avvisa le schermate che la coda e cambiata, senza farle interrogare il disco a vuoto. */
export function subscribeToOutbox(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function announce() {
  for (const listener of [...listeners]) listener();
}

function retryDelay(attempts: number) {
  const steps = Math.min(Math.max(attempts - 1, 0), 8);
  return Math.min(RETRY_MAX_MILLISECONDS, RETRY_BASE_MILLISECONDS * 2 ** steps);
}

function failureMessage(error: unknown) {
  return error instanceof Error && error.message ? error.message : "Invio non riuscito.";
}

function belongsTo(operation: OutboxOperation, owner: OutboxOwner) {
  return operation.companyId === owner.companyId && operation.userId === owner.userId;
}

export async function enqueueOperation(input: NewOutboxOperation): Promise<OutboxOperation> {
  const createdAt = input.createdAt ?? new Date().toISOString();
  const handle = await database();
  const transaction = handle.transaction(STORE, "readwrite");
  const last = await transaction.store.index("sequence").openCursor(null, "prev");
  const operation: OutboxOperation = {
    id: crypto.randomUUID(),
    sequence: (last?.value.sequence ?? 0) + 1,
    companyId: input.companyId,
    userId: input.userId,
    kind: input.kind,
    target: input.target,
    body: input.body ?? null,
    attachment: input.attachment ?? null,
    dependsOn: input.dependsOn ?? null,
    dependsOnField: input.dependsOnField ?? null,
    label: input.label,
    attempts: 0,
    status: "pending",
    lastError: null,
    nextAttemptAt: createdAt,
    createdAt,
  };
  await transaction.store.put(operation);
  await transaction.done;
  announce();
  return operation;
}

/** Tutto quello che il dispositivo tiene per questa persona, in ordine di partenza. */
export async function listOperations(owner: OutboxOwner): Promise<OutboxOperation[]> {
  const handle = await database();
  const records = await handle.getAllFromIndex(STORE, "owner", [owner.companyId, owner.userId]);
  return records.sort((first, second) => first.sequence - second.sequence);
}

export async function countPendingOperations(owner: OutboxOwner) {
  const operations = await listOperations(owner);
  return {
    inAttesa: operations.filter((operation) => operation.status === "pending").length,
    segnalate: operations.filter((operation) => operation.status === "blocked").length,
  };
}

/**
 * Spazio occupato sul dispositivo, in byte.
 *
 * Serve a dimostrare la promessa: dopo un invio riuscito questo numero torna dov'era prima,
 * perche l'elemento non resta come traccia. Le foto sono il motivo per cui la promessa conta.
 */
export async function outboxFootprintBytes(owner: OutboxOwner) {
  const operations = await listOperations(owner);
  return operations.reduce(
    (total, operation) => total + (operation.body?.length ?? 0) + (operation.attachment?.bytes.byteLength ?? 0),
    0,
  );
}

export async function clearOutbox() {
  const handle = await database();
  await handle.clear(STORE);
  announce();
}

function readField(source: unknown, field: string): string | null {
  let current: unknown = source;
  for (const step of field.split(".")) {
    if (current === null || typeof current !== "object") return null;
    current = (current as Record<string, unknown>)[step];
  }
  return typeof current === "string" ? current : null;
}

/**
 * Il segnaposto che un elemento lascia nel proprio indirizzo quando aspetta un altro
 * elemento della coda. Esempio: la foto di una richiesta materiale non conosce il numero
 * della richiesta finche la richiesta non e arrivata al server.
 */
export const DEPENDENCY_PLACEHOLDER = "{{id}}";

async function resolveDependents(operationId: string, result: unknown) {
  const handle = await database();
  const transaction = handle.transaction(STORE, "readwrite");
  let cursor = await transaction.store.openCursor();
  while (cursor) {
    const record = cursor.value;
    if (record.dependsOn === operationId) {
      const value = record.dependsOnField ? readField(result, record.dependsOnField) : null;
      if (value) {
        await cursor.update({
          ...record,
          target: record.target.replaceAll(DEPENDENCY_PLACEHOLDER, value),
          dependsOn: null,
          dependsOnField: null,
        });
      } else {
        await cursor.update({
          ...record,
          status: "blocked",
          lastError: "Il server non ha restituito il riferimento a cui questo file si lega.",
        });
      }
    }
    cursor = await cursor.continue();
  }
  await transaction.done;
}

async function blockDependents(operationId: string, reason: string) {
  const handle = await database();
  const transaction = handle.transaction(STORE, "readwrite");
  let cursor = await transaction.store.openCursor();
  while (cursor) {
    if (cursor.value.dependsOn === operationId && cursor.value.status !== "blocked") {
      await cursor.update({ ...cursor.value, status: "blocked", lastError: reason });
    }
    cursor = await cursor.continue();
  }
  await transaction.done;
}

async function send(operation: OutboxOperation, sender: OutboxSender) {
  if (operation.kind === "upload") {
    if (!operation.attachment) throw new Error("Il file da caricare non c'e piu sul dispositivo.");
    return sender.sendUpload(operation.target, operation.attachment);
  }
  return sender.sendTrpc(operation.target, operation.body ?? "null");
}

let running: Promise<FlushSummary> | null = null;

/**
 * Svuota la coda.
 *
 * Uno alla volta e in ordine di registrazione: due azioni fatte offline arrivano al server
 * nello stesso ordine in cui l'operaio le ha fatte, non in quello che decide la rete.
 * Se la rete cade a meta, il giro si ferma li e riprende da li: gli elementi dopo non
 * vengono nemmeno tentati, e nessuno scavalca nessuno.
 *
 * Due chiamate insieme, per esempio l'evento `online` e il battito periodico, non
 * raddoppiano gli invii: la seconda aspetta l'esito della prima.
 */
export function flushOutbox(
  owner: OutboxOwner,
  sender: OutboxSender,
  options: { now?: () => number } = {},
): Promise<FlushSummary> {
  running ??= drain(owner, sender, options).finally(() => { running = null; });
  return running;
}

async function drain(
  owner: OutboxOwner,
  sender: OutboxSender,
  options: { now?: () => number },
): Promise<FlushSummary> {
  const now = options.now ?? (() => Date.now());
  const summary: FlushSummary = { inviate: 0, rimandate: 0, segnalate: 0 };
  const handle = await database();
  let changed = false;
  let networkDown = false;

  const order = (await listOperations(owner)).map((operation) => operation.id);
  for (const id of order) {
    // Riletto adesso e non all'inizio del giro: l'elemento che dipende da un altro riceve
    // il proprio indirizzo definitivo un istante prima, quando il predecessore arriva.
    const operation = await handle.get(STORE, id);
    if (!operation || !belongsTo(operation, owner)) continue;
    if (operation.status === "blocked") { summary.segnalate += 1; continue; }
    if (networkDown) { summary.rimandate += 1; continue; }
    if (new Date(operation.nextAttemptAt).getTime() > now()) { summary.rimandate += 1; continue; }
    // Il predecessore non e ancora arrivato: questo elemento non ha un indirizzo valido.
    if (operation.dependsOn) { summary.rimandate += 1; continue; }

    try {
      const result = await send(operation, sender);
      await handle.delete(STORE, operation.id);
      await resolveDependents(operation.id, result);
      summary.inviate += 1;
      changed = true;
    } catch (error) {
      if (error instanceof NetworkUnavailableError) {
        // Non e colpa dell'elemento: nessun tentativo contato, e il giro si ferma qui.
        networkDown = true;
        summary.rimandate += 1;
        continue;
      }
      const attempts = operation.attempts + 1;
      const blocked = attempts >= MAX_ATTEMPTS;
      await handle.put(STORE, {
        ...operation,
        attempts,
        lastError: failureMessage(error),
        status: blocked ? "blocked" : "pending",
        nextAttemptAt: new Date(now() + retryDelay(attempts)).toISOString(),
      });
      if (blocked) {
        await blockDependents(operation.id, "L'operazione da cui dipende non e stata inviata.");
        summary.segnalate += 1;
      } else {
        summary.rimandate += 1;
      }
      changed = true;
    }
  }

  if (changed) announce();
  return summary;
}

/** Rimette in gioco un elemento segnalato, su gesto di chi lo ha registrato. */
export async function retryOperation(id: string, owner: OutboxOwner) {
  const handle = await database();
  const stored = await handle.get(STORE, id);
  if (!stored || !belongsTo(stored, owner)) return false;
  await handle.put(STORE, {
    ...stored,
    attempts: 0,
    status: "pending",
    lastError: null,
    nextAttemptAt: new Date().toISOString(),
  });
  announce();
  return true;
}

/** Butta via un elemento segnalato. Lo decide chi lo ha registrato, mai la coda da sola. */
export async function discardOperation(id: string, owner: OutboxOwner) {
  const handle = await database();
  const stored = await handle.get(STORE, id);
  if (!stored || !belongsTo(stored, owner)) return false;
  await handle.delete(STORE, id);
  await blockDependents(id, "L'operazione da cui dipende e stata eliminata dal dispositivo.");
  announce();
  return true;
}
